import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DisciplinaryRecord, Worker } from '../../types/worker';
import { fetchDisciplinary, addDisciplinary, fetchWorkers } from '../../services/workerService';

interface DisciplinaryTabProps {
  farmId: string;
}

const DisciplinaryTab: React.FC<DisciplinaryTabProps> = ({ farmId }) => {
  const [records, setRecords] = useState<DisciplinaryRecord[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newRecord, setNewRecord] = useState<Partial<DisciplinaryRecord>>({
    farmId,
    type: 'warning',
    incidentDate: new Date().toISOString().split('T')[0],
    description: '',
    actionTaken: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      const [recordsData, workersData] = await Promise.all([
        fetchDisciplinary(farmId),
        fetchWorkers(farmId)
      ]);
      setRecords(recordsData);
      setWorkers(workersData);
    } catch (error) {
      console.error('Error loading disciplinary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async () => {
    try {
      if (!newRecord.workerId || !newRecord.type || !newRecord.incidentDate) return;
      
      await addDisciplinary({ ...newRecord, farmId });
      setOpen(false);
      loadData();
      setNewRecord({ 
        farmId, 
        type: 'warning', 
        incidentDate: new Date().toISOString().split('T')[0],
        description: '',
        actionTaken: ''
      });
    } catch (error) {
      console.error('Error adding record:', error);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown Worker';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'termination': return 'error';
      case 'suspension': return 'warning';
      case 'warning': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Disciplinary Records</Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Log Incident
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Action Taken</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No disciplinary records found</TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{getWorkerName(record.workerId)}</TableCell>
                  <TableCell>{record.incidentDate}</TableCell>
                  <TableCell>
                    <Chip 
                      label={record.type.toUpperCase()} 
                      color={getTypeColor(record.type) as any} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{record.description}</TableCell>
                  <TableCell>{record.actionTaken || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Disciplinary Incident</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Worker"
                fullWidth
                value={newRecord.workerId || ''}
                onChange={(e) => setNewRecord({ ...newRecord, workerId: e.target.value })}
              >
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {worker.name} ({worker.role})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Incident Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newRecord.incidentDate}
                onChange={(e) => setNewRecord({ ...newRecord, incidentDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Type"
                fullWidth
                value={newRecord.type}
                onChange={(e) => setNewRecord({ ...newRecord, type: e.target.value as any })}
              >
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="suspension">Suspension</MenuItem>
                <MenuItem value="termination">Termination</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newRecord.description || ''}
                onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Action Taken"
                fullWidth
                value={newRecord.actionTaken || ''}
                onChange={(e) => setNewRecord({ ...newRecord, actionTaken: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleAddRecord}>Log Incident</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisciplinaryTab;
